// Path: src/components/OfficerLayout.jsx
// CHANGE: Added CloudUpload icon import + "Candle Upload" menu item after "Trading Data"

import React, { useContext, useState, useEffect } from 'react';
import { 
  Box, 
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
  Stack,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Circle as CircleIcon,
  Wifi as WifiIcon,
  Lock as LockIcon,
  Dashboard as DashboardIcon,
  AutoAwesome as AutoAwesomeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  ReceiptLong as ReceiptLongIcon,
  Code as CodeIcon,
  GridView as GridViewIcon,
  MonitorHeart as MonitorHeartIcon,
  RocketLaunch as RocketLaunchIcon,
  CloudUpload as CloudUploadIcon,
  Hub as HubIcon,
  LayersClear as LayersClearIcon,
  CandlestickChart as CandlestickChartIcon,
  TrendingUp as TrendingUpIcon,
  Storage as StorageIcon,
  QueryStats as QueryStatsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';
import Collapse from '@mui/material/Collapse';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import FloatingAgentChat from './FloatingAgentChat';

const DRAWER_WIDTH_EXPANDED = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

const OfficerLayout = ({ children }) => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isLargeScreen = useMediaQuery('(min-width:1440px)');
  const [mobileOpen, setMobileOpen] = useState(true);

  // Collapse on small screens, expand on large screens.
  // isLargeScreen is false on small laptops (<1440px) and true on extended monitors.
  const [isCollapsed, setIsCollapsed] = useState(!isLargeScreen);

  // Auto-adjust whenever the screen crosses the 1440px boundary.
  useEffect(() => {
    setIsCollapsed(!isLargeScreen);
  }, [isLargeScreen]);

  const drawerWidth = isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  const menuGroups = [
    {
      key: 'trading',
      label: 'Trading',
      icon: <TrendingUpIcon />,
      color: '#00d4ff',
      items: [
        { label: 'Portfolio Monitor', path: '/officer/bulk-strategies', icon: <DashboardIcon />, badge: 'BULK', badgeColor: '#764ba2' },
        { label: 'Smart Portfolio', path: '/officer/gen-bulk-strategies', icon: <AutoAwesomeIcon />, badge: 'NEW', badgeColor: '#00ff88' },
        { label: 'Simple Trades', path: '/officer/simple-trades', icon: <GridViewIcon />, badge: 'EXCEL', badgeColor: '#00d4ff' },
        { label: 'Trading Data', path: '/officer/trading-data', icon: <RocketLaunchIcon />, badge: 'BUILD', badgeColor: '#f59e0b' },
        { label: 'Strategy Generator', path: '/officer/strategy-generator', icon: <AutoAwesomeIcon />, badge: 'AI', badgeColor: '#f59e0b' },
        { label: 'Composite Generator', path: '/officer/composite-generator', icon: <AutoAwesomeIcon />, badge: 'NEW', badgeColor: '#a78bfa' },
      ],
    },
    {
      key: 'data',
      label: 'Data Tools',
      icon: <StorageIcon />,
      color: '#a855f7',
      items: [
        { label: 'PineScript Hub', path: '/officer/pinescript-hub', icon: <HubIcon />, badge: 'SHARE', badgeColor: '#00d4ff' },
        { label: 'Candle Upload', path: '/officer/candle-upload', icon: <CloudUploadIcon />, badge: 'OHLC', badgeColor: '#a855f7' },
        { label: 'Candle Downloader', path: '/officer/candle-downloader', icon: <CandlestickChartIcon />, badge: 'DATA', badgeColor: '#22c55e' },
      ],
    },
    {
      key: 'monitoring',
      label: 'Monitoring',
      icon: <QueryStatsIcon />,
      color: '#22c55e',
      items: [
        { label: 'All Trades', path: '/officer/trades/all', icon: <ReceiptLongIcon />, badge: 'HISTORY', badgeColor: '#f59e0b' },
        { label: 'Signal Health', path: '/officer/signal-health', icon: <MonitorHeartIcon />, badge: 'LIVE', badgeColor: '#22c55e' },
        { label: 'Signal Launcher', path: '/officer/signal-launcher', icon: <RocketLaunchIcon />, badge: 'TEST', badgeColor: '#f59e0b' },
      ],
    },
    {
      key: 'admin',
      label: 'Admin',
      icon: <AdminPanelSettingsIcon />,
      color: '#ef4444',
      items: [
        { label: 'Strategy Config', path: '/officer/strategy-config', icon: <CodeIcon />, badge: 'CRUD', badgeColor: '#00d4ff' },
        { label: 'Signal Nullify', path: '/officer/signal-nullify', icon: <LayersClearIcon />, badge: 'ADMIN', badgeColor: '#ef4444' },
      ],
    },
  ];

  const isPathActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('officerSidebarGroups') || 'null');
      if (saved) return saved;
    } catch {}
    const initial = {};
    menuGroups.forEach(g => { initial[g.key] = g.items.some(it => isPathActive(it.path)) || g.key === 'trading'; });
    return initial;
  });

  useEffect(() => {
    localStorage.setItem('officerSidebarGroups', JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  // Auto-expand the group that contains the currently active route
  useEffect(() => {
    const activeGroup = menuGroups.find(g => g.items.some(it => isPathActive(it.path)));
    if (activeGroup && !expandedGroups[activeGroup.key]) {
      setExpandedGroups(prev => ({ ...prev, [activeGroup.key]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleCollapseToggle = () => setIsCollapsed(!isCollapsed);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleMenuClick = (item) => {
    if (item.disabled) return;
    navigate(item.path);
    if (isMobile) setMobileOpen(false);
  };

  const drawerContent = (
    <Box sx={{ 
      height: '100%',
      background: 'linear-gradient(180deg, #0a0e14 0%, #050709 100%)',
      display: 'flex',
      flexDirection: 'column',
      borderRight: `1px solid ${alpha('#00d4ff', 0.1)}`,
    }}>
      {/* Logo Header */}
      <Box sx={{ 
        p: isCollapsed ? 1.5 : 2.5,
        pb: isCollapsed ? 1.5 : 2,
        borderBottom: `1px solid ${alpha('#00d4ff', 0.1)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'space-between',
      }}>
        <Stack direction="row" alignItems="center" spacing={isCollapsed ? 0 : 1.5}>
          <Box sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 16px ${alpha('#00d4ff', 0.4)}`,
          }}>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>
              T
            </Typography>
          </Box>
          {!isCollapsed && (
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
                TradeWave
              </Typography>
              <Typography sx={{ color: alpha('#00d4ff', 0.7), fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase' }}>
                Officer Portal
              </Typography>
            </Box>
          )}
        </Stack>
        
        {!isMobile && !isCollapsed && (
          <IconButton
            onClick={handleCollapseToggle}
            size="small"
            sx={{
              width: 28, height: 28,
              bgcolor: alpha('#00d4ff', 0.1),
              border: `1px solid ${alpha('#00d4ff', 0.3)}`,
              '&:hover': { bgcolor: alpha('#00d4ff', 0.2) },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 16, color: '#00d4ff' }} />
          </IconButton>
        )}
      </Box>

      {!isMobile && isCollapsed && (
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
          <IconButton
            onClick={handleCollapseToggle}
            size="small"
            sx={{
              width: 28, height: 28,
              bgcolor: alpha('#00d4ff', 0.1),
              border: `1px solid ${alpha('#00d4ff', 0.3)}`,
              '&:hover': { bgcolor: alpha('#00d4ff', 0.2) },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 16, color: '#00d4ff' }} />
          </IconButton>
        </Box>
      )}

      {/* User Profile */}
      <Box sx={{ px: isCollapsed ? 1 : 2, pt: 2, pb: 2 }}>
        <Box sx={{
          background: alpha('#0f1419', 0.8),
          borderRadius: 2,
          p: isCollapsed ? 1 : 1.5,
          border: `1px solid ${alpha('#00d4ff', 0.2)}`,
          display: 'flex',
          justifyContent: 'center',
        }}>
          <Stack 
            direction={isCollapsed ? 'column' : 'row'} 
            spacing={isCollapsed ? 0 : 1.5} 
            alignItems="center"
            sx={{ width: '100%' }}
          >
            <Avatar sx={{
              width: 36, height: 36,
              background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
              fontWeight: 700, fontSize: '0.95rem',
            }}>
              {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'O'}
            </Avatar>
            {!isCollapsed && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ color: 'grey.100', fontWeight: 600, lineHeight: 1.2 }}>
                  {user?.full_name || user?.username || 'Officer'}
                </Typography>
                <Typography variant="caption" sx={{ color: alpha('#00d4ff', 0.9), fontSize: '0.7rem', fontWeight: 600 }}>
                  {user?.role?.toUpperCase() || 'OFFICER'}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: isCollapsed ? 0.5 : 1.2, pb: 1 }}>
        {menuGroups.map((group) => {
          const hasActive = group.items.some(it => isPathActive(it.path));
          const isOpen = !!expandedGroups[group.key];

          const renderItem = (item) => {
            const isActive = isPathActive(item.path);
            const button = (
              <ListItemButton
                onClick={() => handleMenuClick(item)}
                disabled={item.disabled}
                sx={{
                  borderRadius: 1.5,
                  px: isCollapsed ? 1 : 1.5,
                  py: 0.7,
                  pl: isCollapsed ? 1 : 2.3,
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  position: 'relative',
                  background: isActive ? alpha(group.color, 0.12) : 'transparent',
                  '&::before': !isCollapsed ? {
                    content: '""', position: 'absolute', left: 10, top: 8, bottom: 8, width: 2,
                    borderRadius: 1,
                    background: isActive ? group.color : alpha('#fff', 0.05),
                  } : {},
                  '&:hover': { background: alpha(group.color, 0.08) },
                  opacity: item.disabled ? 0.4 : 1,
                  transition: 'background 0.15s',
                }}
              >
                <ListItemIcon sx={{
                  minWidth: isCollapsed ? 'unset' : 32,
                  justifyContent: 'center',
                  color: isActive ? group.color : 'grey.500',
                  '& > svg': { fontSize: 18 },
                }}>
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={0.8}>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: isActive ? 600 : 500,
                            color: isActive ? group.color : 'grey.300' }}>
                            {item.label}
                          </Typography>
                          {item.badge && (
                            <Chip label={item.badge} size="small" sx={{
                              height: 15, fontSize: '0.55rem', fontWeight: 700,
                              bgcolor: alpha(item.badgeColor, 0.18), color: item.badgeColor,
                              '& .MuiChip-label': { px: 0.7 },
                            }} />
                          )}
                        </Stack>
                      }
                    />
                    {item.disabled && <LockIcon sx={{ fontSize: 13, color: 'grey.800' }} />}
                    {isActive && <CircleIcon sx={{ fontSize: 5, color: group.color }} />}
                  </>
                )}
              </ListItemButton>
            );
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                {isCollapsed ? (
                  <Tooltip title={item.label} placement="right" arrow>
                    <Box sx={{ width: '100%' }}>{button}</Box>
                  </Tooltip>
                ) : button}
              </ListItem>
            );
          };

          // Collapsed mode: flatten (just render items with tooltips, no group header)
          if (isCollapsed) {
            return (
              <Box key={group.key} sx={{ mb: 0.8 }}>
                <Box sx={{ my: 0.6, mx: 1, height: 1, background: alpha(group.color, 0.12) }} />
                <List sx={{ py: 0 }}>{group.items.map(renderItem)}</List>
              </Box>
            );
          }

          return (
            <Box key={group.key} sx={{ mb: 0.6 }}>
              <ListItemButton
                onClick={() => toggleGroup(group.key)}
                sx={{
                  borderRadius: 1.5,
                  px: 1.3, py: 0.9, mb: 0.3,
                  background: hasActive ? alpha(group.color, 0.08) : alpha('#fff', 0.015),
                  border: `1px solid ${hasActive ? alpha(group.color, 0.2) : alpha('#fff', 0.04)}`,
                  '&:hover': { background: alpha(group.color, 0.08), borderColor: alpha(group.color, 0.25) },
                }}
              >
                <ListItemIcon sx={{
                  minWidth: 30, justifyContent: 'center',
                  color: hasActive ? group.color : alpha('#fff', 0.35),
                  '& > svg': { fontSize: 17 },
                }}>
                  {group.icon}
                </ListItemIcon>
                <Typography sx={{
                  flex: 1, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: hasActive ? group.color : alpha('#fff', 0.45),
                }}>
                  {group.label}
                </Typography>
                <Chip label={group.items.length} size="small" sx={{
                  height: 15, fontSize: '0.55rem', fontWeight: 700, mr: 0.6,
                  bgcolor: alpha(hasActive ? group.color : '#fff', 0.1),
                  color: hasActive ? group.color : alpha('#fff', 0.4),
                  '& .MuiChip-label': { px: 0.7 },
                }} />
                <ExpandMoreIcon sx={{
                  fontSize: 17,
                  color: hasActive ? group.color : alpha('#fff', 0.35),
                  transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.2s',
                }} />
              </ListItemButton>
              <Collapse in={isOpen} timeout={180} unmountOnExit>
                <List sx={{ py: 0 }}>{group.items.map(renderItem)}</List>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Status Bar */}
      <Box sx={{ p: isCollapsed ? 1 : 2, pt: 1.5, borderTop: `1px solid ${alpha('#00d4ff', 0.1)}` }}>
        <Stack spacing={1.5}>
          {!isCollapsed && (
            <Box sx={{
              background: alpha('#00ff88', 0.1),
              border: `1px solid ${alpha('#00ff88', 0.2)}`,
              borderRadius: 1.5, p: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#00ff88', boxShadow: `0 0 8px ${alpha('#00ff88', 0.8)}` }} />
                <Typography sx={{ color: '#00ff88', fontSize: '0.75rem', fontWeight: 600 }}>Connected</Typography>
              </Stack>
              <WifiIcon sx={{ fontSize: 14, color: alpha('#00ff88', 0.7) }} />
            </Box>
          )}

          {isCollapsed && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#00ff88', boxShadow: `0 0 8px ${alpha('#00ff88', 0.8)}` }} />
            </Box>
          )}

          <Tooltip title={isCollapsed ? "Logout" : ""} placement="right">
            <Button
              fullWidth variant="outlined"
              startIcon={!isCollapsed && <LogoutIcon sx={{ fontSize: 16 }} />}
              onClick={handleLogout}
              sx={{
                color: 'grey.400', borderColor: alpha('#666', 0.3), py: 1, borderRadius: 1.5,
                textTransform: 'none', fontSize: '0.85rem', fontWeight: 600,
                minWidth: isCollapsed ? 40 : 'auto', px: isCollapsed ? 0 : 2, justifyContent: 'center',
                '&:hover': { borderColor: alpha('#ff4444', 0.5), background: alpha('#ff4444', 0.1), color: '#ff4444' },
              }}
            >
              {isCollapsed ? <LogoutIcon sx={{ fontSize: 16 }} /> : 'Logout'}
            </Button>
          </Tooltip>
          {!isCollapsed && (<Typography component="a" href="/privacy" target="_blank" rel="noopener noreferrer" sx={{ display: 'block', textAlign: 'center', fontSize: '0.7rem', color: 'grey.500', textDecoration: 'none', '&:hover': { color: 'grey.300', textDecoration: 'underline' } }}>Privacy</Typography>)}
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#0f1419', overflow: 'hidden' }}>
      {isMobile && (
        <AppBar 
          position="fixed" 
          elevation={0}
          sx={{ 
            bgcolor: alpha('#0f1419', 0.95),
            backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${alpha('#00d4ff', 0.1)}`,
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
            <IconButton edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, color: 'grey.300' }}>
              <MenuIcon />
            </IconButton>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'grey.50' }}>
              TradeWave Officer
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH_EXPANDED, boxSizing: 'border-box' },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', lg: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { 
            width: drawerWidth, 
            boxSizing: 'border-box',
            transition: 'width 0.3s ease',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          bgcolor: '#0f1419',
          pt: { xs: 7, sm: 8, lg: 0 },
          width: { lg: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        {children}
      </Box>

      <FloatingAgentChat />
    </Box>
  );
};

export default OfficerLayout;






// // src/components/OfficerLayout.jsx - UPDATED WITH SIGNAL LAUNCHER
// import React, { useContext, useState, useEffect } from 'react';
// import { 
//   Box, 
//   Drawer,
//   AppBar,
//   Toolbar,
//   List,
//   ListItem,
//   ListItemIcon,
//   ListItemText,
//   ListItemButton,
//   Typography,
//   IconButton,
//   Avatar,
//   Chip,
//   Button,
//   useMediaQuery,
//   useTheme,
//   Stack,
//   alpha,
//   Tooltip,
//   Divider,
// } from '@mui/material';
// import {
//   Logout as LogoutIcon,
//   Menu as MenuIcon,
//   Circle as CircleIcon,
//   Wifi as WifiIcon,
//   Lock as LockIcon,
//   Dashboard as DashboardIcon,
//   AutoAwesome as AutoAwesomeIcon,
//   Tune as TuneIcon,
//   ChevronLeft as ChevronLeftIcon,
//   ChevronRight as ChevronRightIcon,
//   ReceiptLong as ReceiptLongIcon,
//   Code as CodeIcon,
//   GridView as GridViewIcon,
//   MonitorHeart as MonitorHeartIcon,
//   RocketLaunch as RocketLaunchIcon,
// } from '@mui/icons-material';
// import { useNavigate, useLocation } from 'react-router-dom';
// import AuthContext from '../contexts/AuthContext';

// const DRAWER_WIDTH_EXPANDED = 260;
// const DRAWER_WIDTH_COLLAPSED = 72;

// const OfficerLayout = ({ children }) => {
//   const { user, logout } = useContext(AuthContext);
//   const navigate = useNavigate();
//   const location = useLocation();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
//   const [mobileOpen, setMobileOpen] = useState(true);
  
//   const [isCollapsed, setIsCollapsed] = useState(() => {
//     const saved = localStorage.getItem('sidebarCollapsed');
//     return saved ? JSON.parse(saved) : true;
//   });

//   useEffect(() => {
//     localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
//   }, [isCollapsed]);

//   const drawerWidth = isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

//   const menuItems = [
//     { type: 'divider' },
//     { type: 'section', label: 'TRADING' },
//     {
//       label: 'Portfolio Monitor',
//       path: '/officer/bulk-strategies',
//       icon: <DashboardIcon />,
//       badge: 'BULK',
//       badgeColor: '#764ba2',
//     },
//     {
//       label: 'Smart Portfolio',
//       path: '/officer/gen-bulk-strategies',
//       icon: <AutoAwesomeIcon />,
//       badge: 'NEW',
//       badgeColor: '#00ff88',
//     },
//     {
//       label: 'Simple Trades',
//       path: '/officer/simple-trades',
//       icon: <GridViewIcon />,
//       badge: 'EXCEL',
//       badgeColor: '#00d4ff',
//     },

//     {
//       label: 'Trading Data',
//       path: '/officer/trading-data',
//       icon: <RocketLaunchIcon />,
//       badge: 'BUILD',
//       badgeColor: '#f59e0b',
//     },
    
//     {
//       label: 'All Trades',
//       path: '/officer/trades/all', // all active trades
//       icon: <ReceiptLongIcon />,
//       badge: 'HISTORY',
//       badgeColor: '#f59e0b',
//     },

//     // { type: 'divider' },
//     // { type: 'section', label: 'MONITORING' },
//     {
//       label: 'Signal Health',
//       path: '/officer/signal-health',
//       icon: <MonitorHeartIcon />,
//       badge: 'LIVE',
//       badgeColor: '#22c55e',
//     },
//     // { type: 'divider' },
//     // { type: 'section', label: 'TESTING' },
//     {
//       label: 'Signal Launcher',
//       path: '/officer/signal-launcher',
//       icon: <RocketLaunchIcon />,
//       badge: 'TEST',
//       badgeColor: '#f59e0b',
//     },
//     // { type: 'divider' },
//     // { type: 'section', label: 'CONFIGURATION' },
//     {
//       label: 'Strategy Config',
//       path: '/officer/strategy-config',
//       icon: <CodeIcon />,
//       badge: 'CRUD',
//       badgeColor: '#00d4ff',
//     },
//     // {
//     //   label: 'Strategy Management',
//     //   path: '/officer/strategy-management',
//     //   icon: <TuneIcon />,
//     //   badge: 'MANAGE',
//     //   badgeColor: '#764ba2',
//     // },
//   ];

//   const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
//   const handleCollapseToggle = () => setIsCollapsed(!isCollapsed);

//   const handleLogout = async () => {
//     await logout();
//     navigate('/login');
//   };

//   const isPathActive = (path) => {
//     return location.pathname === path || location.pathname.startsWith(path + '/');
//   };

//   const handleMenuClick = (item) => {
//     if (item.disabled) return;
//     navigate(item.path);
//     if (isMobile) setMobileOpen(false);
//   };

//   const drawerContent = (
//     <Box sx={{ 
//       height: '100%',
//       background: 'linear-gradient(180deg, #0a0e14 0%, #050709 100%)',
//       display: 'flex',
//       flexDirection: 'column',
//       borderRight: `1px solid ${alpha('#00d4ff', 0.1)}`,
//     }}>
//       {/* Logo Header */}
//       <Box sx={{ 
//         p: isCollapsed ? 1.5 : 2.5,
//         pb: isCollapsed ? 1.5 : 2,
//         borderBottom: `1px solid ${alpha('#00d4ff', 0.1)}`,
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: isCollapsed ? 'center' : 'space-between',
//       }}>
//         <Stack direction="row" alignItems="center" spacing={isCollapsed ? 0 : 1.5}>
//           <Box sx={{
//             width: 36,
//             height: 36,
//             borderRadius: 1.5,
//             background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             boxShadow: `0 4px 16px ${alpha('#00d4ff', 0.4)}`,
//           }}>
//             <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>
//               T
//             </Typography>
//           </Box>
//           {!isCollapsed && (
//             <Box>
//               <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
//                 TradeWave
//               </Typography>
//               <Typography sx={{ color: alpha('#00d4ff', 0.7), fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase' }}>
//                 Officer Portal
//               </Typography>
//             </Box>
//           )}
//         </Stack>
        
//         {!isMobile && !isCollapsed && (
//           <IconButton
//             onClick={handleCollapseToggle}
//             size="small"
//             sx={{
//               width: 28, height: 28,
//               bgcolor: alpha('#00d4ff', 0.1),
//               border: `1px solid ${alpha('#00d4ff', 0.3)}`,
//               '&:hover': { bgcolor: alpha('#00d4ff', 0.2) },
//             }}
//           >
//             <ChevronLeftIcon sx={{ fontSize: 16, color: '#00d4ff' }} />
//           </IconButton>
//         )}
//       </Box>

//       {!isMobile && isCollapsed && (
//         <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
//           <IconButton
//             onClick={handleCollapseToggle}
//             size="small"
//             sx={{
//               width: 28, height: 28,
//               bgcolor: alpha('#00d4ff', 0.1),
//               border: `1px solid ${alpha('#00d4ff', 0.3)}`,
//               '&:hover': { bgcolor: alpha('#00d4ff', 0.2) },
//             }}
//           >
//             <ChevronRightIcon sx={{ fontSize: 16, color: '#00d4ff' }} />
//           </IconButton>
//         </Box>
//       )}

//       {/* User Profile */}
//       <Box sx={{ px: isCollapsed ? 1 : 2, pt: 2, pb: 2 }}>
//         <Box sx={{
//           background: alpha('#0f1419', 0.8),
//           borderRadius: 2,
//           p: isCollapsed ? 1 : 1.5,
//           border: `1px solid ${alpha('#00d4ff', 0.2)}`,
//           display: 'flex',
//           justifyContent: 'center',
//         }}>
//           <Stack 
//             direction={isCollapsed ? 'column' : 'row'} 
//             spacing={isCollapsed ? 0 : 1.5} 
//             alignItems="center"
//             sx={{ width: '100%' }}
//           >
//             <Avatar sx={{
//               width: 36, height: 36,
//               background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
//               fontWeight: 700, fontSize: '0.95rem',
//             }}>
//               {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'O'}
//             </Avatar>
//             {!isCollapsed && (
//               <Box sx={{ flex: 1, minWidth: 0 }}>
//                 <Typography variant="subtitle2" sx={{ color: 'grey.100', fontWeight: 600, lineHeight: 1.2 }}>
//                   {user?.full_name || user?.username || 'Officer'}
//                 </Typography>
//                 <Typography variant="caption" sx={{ color: alpha('#00d4ff', 0.9), fontSize: '0.7rem', fontWeight: 600 }}>
//                   {user?.role?.toUpperCase() || 'OFFICER'}
//                 </Typography>
//               </Box>
//             )}
//           </Stack>
//         </Box>
//       </Box>

//       {/* Navigation Menu */}
//       <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: isCollapsed ? 0.5 : 1.5 }}>
//         <List sx={{ py: 0 }}>
//           {menuItems.map((item, index) => {
//             if (item.type === 'divider') {
//               return <Divider key={`divider-${index}`} sx={{ my: 0.5, mx: 1, borderColor: alpha('#00d4ff', 0.08) }} />;
//             }

//             if (item.type === 'section') {
//               if (isCollapsed) return null;
//               return (
//                 <Box key={`section-${index}`} sx={{ px: 2, pt: 1, pb: 0.5 }}>
//                   <Typography variant="caption" sx={{ color: alpha('#00d4ff', 0.5), fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.1em' }}>
//                     {item.label}
//                   </Typography>
//                 </Box>
//               );
//             }

//             const isActive = isPathActive(item.path);
            
//             const menuButton = (
//               <ListItemButton
//                 onClick={() => handleMenuClick(item)}
//                 disabled={item.disabled}
//                 sx={{
//                   borderRadius: 1.5,
//                   px: isCollapsed ? 1 : 1.5,
//                   py: 1,
//                   justifyContent: isCollapsed ? 'center' : 'flex-start',
//                   background: isActive ? alpha('#00d4ff', 0.15) : 'transparent',
//                   border: `1px solid ${isActive ? alpha('#00d4ff', 0.3) : 'transparent'}`,
//                   '&:hover': { background: alpha('#00d4ff', 0.1), borderColor: alpha('#00d4ff', 0.2) },
//                   opacity: item.disabled ? 0.4 : 1,
//                 }}
//               >
//                 <ListItemIcon sx={{ 
//                   minWidth: isCollapsed ? 'unset' : 36,
//                   justifyContent: 'center',
//                   color: isActive ? '#00d4ff' : 'grey.500',
//                   '& > svg': { fontSize: 20 },
//                 }}>
//                   {item.icon}
//                 </ListItemIcon>
//                 {!isCollapsed && (
//                   <>
//                     <ListItemText 
//                       primary={
//                         <Stack direction="row" alignItems="center" spacing={1}>
//                           <Typography sx={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 500, color: isActive ? '#00d4ff' : 'grey.300' }}>
//                             {item.label}
//                           </Typography>
//                           {item.badge && (
//                             <Chip label={item.badge} size="small" sx={{
//                               height: 16, fontSize: '0.6rem', fontWeight: 700,
//                               bgcolor: alpha(item.badgeColor, 0.2), color: item.badgeColor,
//                               '& .MuiChip-label': { px: 0.8 },
//                             }} />
//                           )}
//                         </Stack>
//                       }
//                     />
//                     {item.disabled && <LockIcon sx={{ fontSize: 14, color: 'grey.800' }} />}
//                     {isActive && <CircleIcon sx={{ fontSize: 5, color: '#00d4ff' }} />}
//                   </>
//                 )}
//               </ListItemButton>
//             );

//             return (
//               <ListItem key={item.path || `item-${index}`} disablePadding sx={{ mb: 0.3 }}>
//                 {isCollapsed ? (
//                   <Tooltip title={item.label} placement="right" arrow>
//                     <Box sx={{ width: '100%' }}>{menuButton}</Box>
//                   </Tooltip>
//                 ) : menuButton}
//               </ListItem>
//             );
//           })}
//         </List>
//       </Box>

//       {/* Status Bar */}
//       <Box sx={{ p: isCollapsed ? 1 : 2, pt: 1.5, borderTop: `1px solid ${alpha('#00d4ff', 0.1)}` }}>
//         <Stack spacing={1.5}>
//           {!isCollapsed && (
//             <Box sx={{
//               background: alpha('#00ff88', 0.1),
//               border: `1px solid ${alpha('#00ff88', 0.2)}`,
//               borderRadius: 1.5, p: 1,
//               display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//             }}>
//               <Stack direction="row" spacing={1} alignItems="center">
//                 <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#00ff88', boxShadow: `0 0 8px ${alpha('#00ff88', 0.8)}` }} />
//                 <Typography sx={{ color: '#00ff88', fontSize: '0.75rem', fontWeight: 600 }}>Connected</Typography>
//               </Stack>
//               <WifiIcon sx={{ fontSize: 14, color: alpha('#00ff88', 0.7) }} />
//             </Box>
//           )}

//           {isCollapsed && (
//             <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
//               <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#00ff88', boxShadow: `0 0 8px ${alpha('#00ff88', 0.8)}` }} />
//             </Box>
//           )}

//           <Tooltip title={isCollapsed ? "Logout" : ""} placement="right">
//             <Button
//               fullWidth variant="outlined"
//               startIcon={!isCollapsed && <LogoutIcon sx={{ fontSize: 16 }} />}
//               onClick={handleLogout}
//               sx={{
//                 color: 'grey.400', borderColor: alpha('#666', 0.3), py: 1, borderRadius: 1.5,
//                 textTransform: 'none', fontSize: '0.85rem', fontWeight: 600,
//                 minWidth: isCollapsed ? 40 : 'auto', px: isCollapsed ? 0 : 2, justifyContent: 'center',
//                 '&:hover': { borderColor: alpha('#ff4444', 0.5), background: alpha('#ff4444', 0.1), color: '#ff4444' },
//               }}
//             >
//               {isCollapsed ? <LogoutIcon sx={{ fontSize: 16 }} /> : 'Logout'}
//             </Button>
//           </Tooltip>
//         </Stack>
//       </Box>
//     </Box>
//   );

//   return (
//     <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#0f1419', overflow: 'hidden' }}>
//       {/* Mobile Header */}
//       {isMobile && (
//         <AppBar 
//           position="fixed" 
//           elevation={0}
//           sx={{ 
//             bgcolor: alpha('#0f1419', 0.95),
//             backdropFilter: 'blur(10px)',
//             borderBottom: `1px solid ${alpha('#00d4ff', 0.1)}`,
//             zIndex: theme.zIndex.drawer + 1,
//           }}
//         >
//           <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
//             <IconButton edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, color: 'grey.300' }}>
//               <MenuIcon />
//             </IconButton>
//             <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'grey.50' }}>
//               TradeWave Officer
//             </Typography>
//           </Toolbar>
//         </AppBar>
//       )}

//       {/* Mobile Drawer */}
//       <Drawer
//         variant="temporary"
//         open={mobileOpen}
//         onClose={handleDrawerToggle}
//         ModalProps={{ keepMounted: true }}
//         sx={{
//           display: { xs: 'block', lg: 'none' },
//           '& .MuiDrawer-paper': { width: DRAWER_WIDTH_EXPANDED, boxSizing: 'border-box' },
//         }}
//       >
//         {drawerContent}
//       </Drawer>

//       {/* Desktop Drawer */}
//       <Drawer
//         variant="permanent"
//         sx={{
//           display: { xs: 'none', lg: 'block' },
//           width: drawerWidth,
//           flexShrink: 0,
//           '& .MuiDrawer-paper': { 
//             width: drawerWidth, 
//             boxSizing: 'border-box',
//             transition: 'width 0.3s ease',
//           },
//         }}
//       >
//         {drawerContent}
//       </Drawer>

//       {/* Main Content - scrollable container */}
//       <Box
//         component="main"
//         sx={{
//           flexGrow: 1,
//           height: '100vh',
//           overflowY: 'auto',
//           overflowX: 'hidden',
//           bgcolor: '#0f1419',
//           pt: { xs: 7, sm: 8, lg: 0 },
//           width: { lg: `calc(100% - ${drawerWidth}px)` },
//         }}
//       >
//         {children}
//       </Box>
//     </Box>
//   );
// };

// export default OfficerLayout;
